import React, { useState } from 'react';
import { Building2, User, MapPin, FileText, CheckCircle, Loader2, AlertCircle, ArrowLeft, Gift, Hammer, Truck, CreditCard, Warehouse } from 'lucide-react';
import { submitRetailerRequest } from '../services/retailerService';
import { Address } from '../types';

interface RetailerApplicationFormProps {
  onBack: () => void;
}

const BUSINESS_TYPES = [
  'Hardware Store',
  'Garden Center',
  'Outdoor / Camping Retailer',
  'Farm Supply Store',
  'General Merchandise',
  'Online Retailer',
  'Landscaping Company',
  'Other',
];

const PROVINCES: { code: string; name: string }[] = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

const RetailerApplicationForm: React.FC<RetailerApplicationFormProps> = ({ onBack }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [taxId, setTaxId] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState<Address>({
    street: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Canada',
  });
  // Optional qualifying fields — let internal team tier inbound leads
  // for 24h vs 7-day response priority. Reviewer recommendation.
  const [yearsInBusiness, setYearsInBusiness] = useState('');
  const [numLocations, setNumLocations] = useState('');
  const [estFirstOrder, setEstFirstOrder] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!businessName || !businessType || !taxId || !contactName || !email || !phone) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!address.street || !address.city || !address.province || !address.postalCode) {
      setError('Please complete your business address.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Append optional qualifying info as `wholesaleProfile`. Backend may not
      // currently consume these fields — Firestore stores them anyway and the
      // internal team can use them for lead tiering off the document.
      const wholesaleProfile: Record<string, string> = {};
      if (yearsInBusiness) wholesaleProfile.yearsInBusiness = yearsInBusiness;
      if (numLocations) wholesaleProfile.numberOfLocations = numLocations;
      if (estFirstOrder) wholesaleProfile.estimatedFirstOrder = estFirstOrder;

      await submitRetailerRequest({
        businessName,
        businessType,
        taxId,
        contactName,
        email,
        phone,
        address,
        ...(Object.keys(wholesaleProfile).length ? { wholesaleProfile } : {}),
      } as any);
      setIsSubmitted(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150';

  const labelClass = 'block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2';

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-paper py-20 lg:py-32 px-6">
        <div className="max-w-prose mx-auto">
          <div className="bg-paper-bright border border-ink-200 p-10 lg:p-14 text-center shadow-rest">
            <div className="w-16 h-16 bg-olive/15 rounded-full flex items-center justify-center mx-auto mb-7">
              <CheckCircle className="w-7 h-7 text-olive" strokeWidth={1.5} />
            </div>
            <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-4">
              — Application received
            </p>
            <h2 className="font-editorial italic font-light text-4xl text-ink-950 leading-tight tracking-tight mb-5">
              Thank you.
            </h2>
            <p className="text-ink-700 text-base leading-[1.65] mb-10 max-w-prose mx-auto">
              Thank you for your interest in becoming a Machete Man retailer. Our team will review your
              application and contact you within 2–3 business days.
            </p>

            <div className="bg-paper border border-ink-200 p-6 mb-10 text-left">
              <p className="font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-4">
                — What happens next
              </p>
              <ol className="space-y-3 text-sm text-ink-900">
                <li className="flex gap-3">
                  <span className="font-mono text-ink-500 flex-shrink-0">01</span>
                  <span>Our team reviews your business information</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono text-ink-500 flex-shrink-0">02</span>
                  <span>We verify your tax ID / HST number</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono text-ink-500 flex-shrink-0">03</span>
                  <span>Upon approval, you receive an email with your login credentials</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono text-ink-500 flex-shrink-0">04</span>
                  <span>Sign in to access wholesale pricing and place orders</span>
                </li>
              </ol>
            </div>

            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 text-ember hover:text-ember-deep text-sm font-medium transition-colors duration-150"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
              Return home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper py-16 lg:py-24 px-6">
      <div className="max-w-3xl mx-auto">

        {/* Header */}
        <div className="mb-12 lg:mb-16">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 text-sm text-ink-700 hover:text-ember transition-colors duration-150 mb-8"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
            Back
          </button>
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-5">
            — Wholesale application
          </p>
          <h1 className="font-editorial italic font-light text-5xl lg:text-6xl text-ink-950 leading-[1.05] tracking-tight mb-6">
            Apply for an account.
          </h1>
          <p className="text-ink-700 text-lg leading-[1.65] max-w-prose">
            Become a Machete Man retailer to access wholesale pricing on authentic Colombian
            machetes, forged by Incolma since 1960. We are Incolma's exclusive Canadian
            distributor — your account is the country's direct line to the forge.
          </p>
        </div>

        {/* Announcement — Scarborough warehouse & retail, move-in Oct 1 2026.
            (Replaced the free-mill-file promo card, which expired Aug 10 2026.) */}
        <div className="bg-forge text-paper border border-brass/40 mb-6 overflow-hidden relative shadow-rest">
          <div className="h-1 bg-brass" />
          <div className="p-6 lg:p-8">
            <p className="font-mono text-[10px] tracking-micro uppercase text-brass mb-3 inline-flex items-center gap-2 flex-wrap">
              <Warehouse className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>New home · October 2026</span>
            </p>
            <h2 className="font-editorial italic text-3xl lg:text-4xl text-paper-warm leading-[1.1] tracking-tight mb-4">
              A warehouse built for your orders.
            </h2>
            <p className="text-paper/80 text-sm lg:text-[15px] leading-relaxed max-w-prose">
              We move into our new Scarborough warehouse &amp; retail location on{' '}
              <strong className="text-paper-warm font-medium">October 1, 2026</strong> — deeper standing
              stock across the full Incolma line, faster fulfillment, and will-call pickup for GTA
              wholesale accounts. Apply now and your account is ready the day the racks are stocked.
            </p>
          </div>
        </div>

        {/* Trust strip — heritage, logistics, payment. Sits between offer and form. */}
        <div className="mb-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-ink-700 border-y border-ink-200 py-5 px-4">
          <div className="inline-flex items-center gap-2">
            <Hammer className="w-4 h-4 text-ember" strokeWidth={1.5} />
            <span>Forged by Incolma · Colombia, since 1960</span>
          </div>
          <div className="inline-flex items-center gap-2">
            <Truck className="w-4 h-4 text-ember" strokeWidth={1.5} />
            <span>Ships from Greater Toronto Area</span>
          </div>
          <div className="inline-flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-ember" strokeWidth={1.5} />
            <span>Interac e-Transfer accepted</span>
          </div>
        </div>

        {/* Requirements — compact 1-line summary (collapsed from 4-bullet card
            to push form within 1.5 viewport heights per reviewer recommendation) */}
        <div className="bg-paper-bright border-l-4 border-brass border-y border-r border-ink-200 px-5 py-3 mb-10 text-sm text-ink-900">
          <span className="font-mono text-[10px] tracking-micro uppercase text-brass mr-3">— Need</span>
          Business HST # · Physical retail or online presence · MOQ 12 (mix-and-match) · Canadian Interac e-Transfer
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-paper-bright border border-ink-200 shadow-rest">

          {error && (
            <div className="bg-ember-soft/25 border-b border-ember/30 p-4 flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-ember flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <p className="text-ember-deep text-sm">{error}</p>
            </div>
          )}

          {/* Business */}
          <section className="p-7 lg:p-10 border-b border-ink-200">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="w-4 h-4 text-ember" strokeWidth={1.5} />
              <h2 className="font-sans font-semibold text-xl text-ink-950 tracking-tight">
                Business
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Business name <span className="text-ember">*</span>
                </label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className={inputClass}
                  placeholder="Your business name"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>
                  Business type <span className="text-ember">*</span>
                </label>
                <select
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)}
                  className={inputClass}
                  required
                >
                  <option value="">Select business type</option>
                  {BUSINESS_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  HST / business number <span className="text-ember">*</span>
                </label>
                <input
                  type="text"
                  value={taxId}
                  onChange={(e) => setTaxId(e.target.value)}
                  className={`${inputClass} font-mono`}
                  placeholder="123456789 RT0001"
                  required
                />
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="p-7 lg:p-10 border-b border-ink-200">
            <div className="flex items-center gap-3 mb-6">
              <User className="w-4 h-4 text-ember" strokeWidth={1.5} />
              <h2 className="font-sans font-semibold text-xl text-ink-950 tracking-tight">
                Contact
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Contact name <span className="text-ember">*</span>
                </label>
                <input
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className={inputClass}
                  placeholder="Full name"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>
                  Email address <span className="text-ember">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder="you@business.com"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>
                  Phone number <span className="text-ember">*</span>
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={inputClass}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
            </div>
          </section>

          {/* Address */}
          <section className="p-7 lg:p-10 border-b border-ink-200">
            <div className="flex items-center gap-3 mb-6">
              <MapPin className="w-4 h-4 text-ember" strokeWidth={1.5} />
              <h2 className="font-sans font-semibold text-xl text-ink-950 tracking-tight">
                Address
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className={labelClass}>
                  Street address <span className="text-ember">*</span>
                </label>
                <input
                  type="text"
                  value={address.street}
                  onChange={(e) => setAddress((p) => ({ ...p, street: e.target.value }))}
                  className={inputClass}
                  placeholder="123 Main Street"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>
                  City <span className="text-ember">*</span>
                </label>
                <input
                  type="text"
                  value={address.city}
                  onChange={(e) => setAddress((p) => ({ ...p, city: e.target.value }))}
                  className={inputClass}
                  placeholder="City"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>
                  Province <span className="text-ember">*</span>
                </label>
                <select
                  value={address.province}
                  onChange={(e) => setAddress((p) => ({ ...p, province: e.target.value }))}
                  className={inputClass}
                  required
                >
                  <option value="">Select province</option>
                  {PROVINCES.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>
                  Postal code <span className="text-ember">*</span>
                </label>
                <input
                  type="text"
                  value={address.postalCode}
                  onChange={(e) =>
                    setAddress((p) => ({ ...p, postalCode: e.target.value.toUpperCase() }))
                  }
                  className={`${inputClass} uppercase`}
                  placeholder="A1A 1A1"
                  maxLength={7}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Country</label>
                <input
                  type="text"
                  value={address.country}
                  onChange={(e) => setAddress((p) => ({ ...p, country: e.target.value }))}
                  className={`${inputClass} bg-paper text-ink-500 cursor-not-allowed`}
                  disabled
                />
              </div>
            </div>
          </section>

          {/* Wholesale Fit — optional qualifying fields. Reviewer's recommendation:
              "Optional ≠ friction, but enables tiering." Each is OPTIONAL — labels
              omit the asterisk and read "(optional)" to make that explicit. */}
          <section className="p-7 lg:p-10 border-b border-ink-200">
            <div className="flex items-center gap-3 mb-2">
              <Gift className="w-4 h-4 text-ember" strokeWidth={1.5} />
              <h2 className="font-sans font-semibold text-xl text-ink-950 tracking-tight">
                Wholesale fit
              </h2>
            </div>
            <p className="text-sm text-ink-500 mb-6">
              Help us prioritize a fast response. All fields optional.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className={labelClass}>Years in business <span className="text-ink-500 normal-case tracking-normal">(optional)</span></label>
                <select
                  value={yearsInBusiness}
                  onChange={(e) => setYearsInBusiness(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select…</option>
                  <option value="Less than 1 year">Less than 1 year</option>
                  <option value="1–3 years">1–3 years</option>
                  <option value="4–10 years">4–10 years</option>
                  <option value="10+ years">10+ years</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Locations <span className="text-ink-500 normal-case tracking-normal">(optional)</span></label>
                <select
                  value={numLocations}
                  onChange={(e) => setNumLocations(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select…</option>
                  <option value="1 location">1 location</option>
                  <option value="2–5 locations">2–5 locations</option>
                  <option value="6+ locations">6+ locations</option>
                  <option value="Online only">Online only</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Est. first order <span className="text-ink-500 normal-case tracking-normal">(optional)</span></label>
                <select
                  value={estFirstOrder}
                  onChange={(e) => setEstFirstOrder(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Select…</option>
                  <option value="12–23 units (MOQ)">12–23 units (MOQ)</option>
                  <option value="24–49 units">24–49 units</option>
                  <option value="50–99 units">50–99 units</option>
                  <option value="100+ units">100+ units</option>
                </select>
              </div>
            </div>
          </section>

          {/* Terms + Submit */}
          <section className="p-7 lg:p-10 bg-paper">
            <div className="flex items-start gap-3 mb-6">
              <input
                type="checkbox"
                id="terms"
                required
                className="mt-1 w-4 h-4 accent-ember border-ink-300"
              />
              <label htmlFor="terms" className="text-sm text-ink-700 leading-relaxed">
                I confirm that the information provided is accurate and I am authorized to apply on
                behalf of this business. I understand that The Machete Man reserves the right to
                verify this information and approve or decline this application.
              </label>
            </div>

            {/* Inline reminder — the region-exclusive supply argument, right where
                conviction is needed most. */}
            <div className="bg-forge text-paper-warm border-l-4 border-brass p-4 mb-5 text-sm flex items-start gap-3">
              <Gift className="w-4 h-4 text-brass mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <span>
                <strong className="text-paper-warm font-medium">The only Incolma source in Canada.</strong>{' '}
                Approved accounts order direct from the country&apos;s exclusive distributor — most
                applications are approved within one business day.
              </span>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-4 bg-ember hover:bg-ember-deep disabled:opacity-50 disabled:cursor-not-allowed text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial inline-flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                  Submitting application...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" strokeWidth={1.5} />
                  Submit application
                </>
              )}
            </button>

            {/* "What happens next" expectation-setting line — reviewer SHOULD-FIX */}
            <p className="mt-4 text-xs text-ink-500 text-center">
              We&rsquo;ll respond within 2–3 business days via WhatsApp or email.
            </p>
          </section>
        </form>

        {/* Help */}
        <p className="text-center text-sm text-ink-700 mt-10">
          Questions? Contact{' '}
          <a
            href="mailto:info@themacheteman.com"
            className="text-ember hover:text-ember-deep transition-colors duration-150 underline underline-offset-4 decoration-ember/30"
          >
            info@themacheteman.com
          </a>
        </p>
      </div>
    </div>
  );
};

export default RetailerApplicationForm;
